import { NAMESPACES } from '../namespaces.js';
import { fanOut } from './fan-out.js';
import { mergeByTimestamp } from './merge-by-timestamp.js';
import { planReadRepair } from './read-repair.js';
import { runRepair } from './repair-runner.js';
export class StorageRouter {
    _providers;
    onProviderError;
    readRepair;
    concurrency;
    now;
    repairedNamespaces = new Set();
    progressListeners = new Set();
    constructor(opts) {
        this._providers = opts.providers;
        this.onProviderError = opts.onProviderError;
        this.readRepair = opts.readRepair ?? 'once-per-session';
        this.concurrency = opts.readRepairConcurrency ?? 4;
        this.now = opts.now ?? (() => new Date().toISOString());
    }
    get providers() {
        return this._providers;
    }
    /** Subscribe to sync progress events. Returns an unsubscribe function. */
    onProgress(listener) {
        this.progressListeners.add(listener);
        return () => this.progressListeners.delete(listener);
    }
    putRecord(rec) {
        return fanOut(this._providers, 'putRecord', (p) => p.putRecord(rec), this.onProviderError);
    }
    async getRecord(ns, key) {
        for (const p of this._providers) {
            try {
                const rec = await p.getRecord(ns, key);
                if (rec)
                    return rec.deletedAt ? null : rec;
            }
            catch (err) {
                this.onProviderError?.(p.id, 'getRecord', err);
            }
        }
        return null;
    }
    deleteRecord(ns, key) {
        const deletedAt = this.now();
        return fanOut(this._providers, 'deleteRecord', (p) => p.deleteRecord(ns, key, deletedAt), this.onProviderError);
    }
    async listAll(ns) {
        const perProvider = await this.collectRefs(ns);
        const winners = mergeByTimestamp(perProvider);
        const records = await this.fetchWinners(winners);
        if (this.shouldRunRepair(ns)) {
            const actions = planReadRepair(winners, perProvider);
            await runRepair(actions, this._providers, this.concurrency, this.onProviderError);
            this.repairedNamespaces.add(ns);
        }
        return records;
    }
    putBlob(ns, key, blob) {
        const updatedAt = this.now();
        return fanOut(this._providers, 'putBlob', (p) => p.putBlob(ns, key, blob, updatedAt), this.onProviderError);
    }
    async getBlob(ns, key) {
        for (const p of this._providers) {
            try {
                const b = await p.getBlob(ns, key);
                if (b)
                    return b;
            }
            catch (err) {
                this.onProviderError?.(p.id, 'getBlob', err);
            }
        }
        return null;
    }
    async forceRepair(ns) {
        const namespaces = ns ? [ns] : Array.from(NAMESPACES);
        const syncStart = Date.now();
        // Invalidate per-session caches so re-discovery catches externally-modified
        // state (e.g. user trashed a Drive folder).
        for (const p of this._providers)
            p.invalidateCache?.();
        this.emit({
            kind: 'sync-start',
            namespaces,
            providerIds: this._providers.map((p) => p.id),
        });
        for (let i = 0; i < namespaces.length; i++) {
            const namespace = namespaces[i];
            const nsStart = Date.now();
            this.emit({ kind: 'namespace-start', namespace, index: i, total: namespaces.length });
            this.repairedNamespaces.delete(namespace);
            const perProvider = await this.collectRefs(namespace);
            for (const { providerId, refs } of perProvider) {
                this.emit({ kind: 'listed', namespace, providerId, count: refs.length });
            }
            const winners = mergeByTimestamp(perProvider);
            this.emit({ kind: 'merged', namespace, winners: winners.size });
            const actions = planReadRepair(winners, perProvider);
            const writes = actions.filter((a) => !a.isTombstone).length;
            const tombstones = actions.length - writes;
            if (actions.length > 0) {
                this.emit({ kind: 'repair-plan', namespace, writes, tombstones });
            }
            for (const a of actions) {
                this.emit({
                    kind: 'repair-step',
                    namespace,
                    providerId: a.providerId,
                    action: a.isTombstone ? 'tombstone' : 'write',
                    key: a.key,
                });
            }
            await runRepair(actions, this._providers, this.concurrency, this.onProviderError);
            for (const p of this._providers) {
                try {
                    await p.reconcile?.(namespace);
                }
                catch (err) {
                    this.onProviderError?.(p.id, 'reconcile', err);
                }
            }
            this.repairedNamespaces.add(namespace);
            this.emit({ kind: 'namespace-done', namespace, durationMs: Date.now() - nsStart });
        }
        this.emit({ kind: 'sync-done', durationMs: Date.now() - syncStart });
    }
    emit(event) {
        for (const listener of this.progressListeners)
            listener(event);
    }
    async collectRefs(ns) {
        const settled = await Promise.allSettled(this._providers.map(async (p) => ({ providerId: p.id, refs: await p.listRefs(ns) })));
        const out = [];
        settled.forEach((r, i) => {
            if (r.status === 'fulfilled')
                out.push(r.value);
            else
                this.onProviderError?.(this._providers[i].id, 'listRefs', r.reason);
        });
        return out;
    }
    async fetchWinners(winners) {
        const out = [];
        for (const w of winners.values()) {
            if (w.deletedAt)
                continue;
            const source = this._providers.find((p) => p.id === w.providerId);
            if (!source)
                continue;
            try {
                const rec = await source.getRecord(w.namespace, w.key);
                if (rec && !rec.deletedAt)
                    out.push(rec);
            }
            catch (err) {
                this.onProviderError?.(source.id, 'getRecord', err);
            }
        }
        return out;
    }
    shouldRunRepair(ns) {
        if (this.readRepair === 'off')
            return false;
        if (this.readRepair === 'on-read')
            return true;
        return !this.repairedNamespaces.has(ns);
    }
}
//# sourceMappingURL=storage-router.js.map